import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { DataContainer } from 'components/DataContainer';
import { calculatePriceAfterDiscount, getDiscountTextStyle } from 'components/Discount';
import { PriceErrorDisplay } from 'components/ReferencePriceSection';
import { isBulkCertasOil, isDrumCertasOil } from 'components/StockInfo/catalogStockUtils';
import { StockDisplayBasic } from 'components/StockInfo/StockDisplay';
import {
  getEstimateB2BReferenceDiscount,
  getEstimateB2BReferencePrice,
  getEstimateB2BReferenceStock,
} from 'domains/estimate/Estimate.store';
import { NON_MKTP } from 'domains/references';
import { getUserCountry } from 'domains/user';
import { CenterFlex, Flex, Spin, Text } from 'UI';
import { getSearchData, textFormatter } from 'utils';

export const ProductPriceB2b = ({
  referenceNumber,
  currency,
  estimateId,
  'data-cy': dataCy,
}: {
  referenceNumber: string;
  currency?: string;
  estimateId: string;
  'data-cy'?: string;
}) => {
  const price = useSelector((state: RootState) => getEstimateB2BReferencePrice(state, { estimateId, referenceNumber }));
  const priceData = getSearchData(price);
  return (
    <DataContainer
      data={price?.searchStatus}
      Loading={() => <Spin size={'small'} />}
      ErrorState={() => <PriceErrorDisplay narrow />}
    >
      <Text type="base" data-cy={dataCy}>
        {textFormatter.formatCurrency(priceData?.clientView?.recommendedPriceVatExcluded ?? 0, currency)}
      </Text>
    </DataContainer>
  );
};

export const B2bStockDisplay = ({
  referenceNumber,
  referenceName,
  estimateId,
  quantity,
  familyCode,
  sellerNumber,
  'data-cy': dataCy,
}: {
  referenceNumber: string;
  referenceName: string;
  estimateId: string;
  quantity: number;
  familyCode?: string;
  sellerNumber?: string;
  'data-cy'?: string;
}) => {
  const stocks = useSelector((state: RootState) =>
    getEstimateB2BReferenceStock(state, {
      estimateId,
      referenceNumber,
    }),
  );
  const userCountry = useSelector(getUserCountry);
  const isDrumOil = isDrumCertasOil(familyCode, undefined, referenceName);
  const isBulkOil = isBulkCertasOil(familyCode, undefined, referenceName);
  const stocksArray = stocks?.data?.get(sellerNumber ?? NON_MKTP);
  return (
    <Flex data-cy={dataCy}>
      <StockDisplayBasic
        stock={{ data: stocksArray, searchStatus: stocks?.searchStatus, errorDetail: stocks?.errorDetail }}
        isDrumOil={isDrumOil}
        isBulkOil={isBulkOil}
        quantity={quantity}
        userCountry={userCountry}
        narrow
      />
    </Flex>
  );
};

export const DiscountedPrice = ({
  referenceNumber,
  quantity,
  estimateId,
  currency,
  'data-cy': dataCy,
}: {
  referenceNumber: string;
  quantity?: number;
  estimateId: string;
  currency?: string;
  'data-cy'?: string;
}) => {
  const price = useSelector((state: RootState) => getEstimateB2BReferencePrice(state, { estimateId, referenceNumber }));
  const discount = useSelector((state: RootState) =>
    getEstimateB2BReferenceDiscount(state, { estimateId, referenceNumber }),
  );
  return (
    <CenterFlex>
      <DataContainer data={price?.searchStatus} ErrorState={() => <PriceErrorDisplay narrow />}>
        <Text displayStyle={getDiscountTextStyle(discount)} type="base" data-cy={dataCy}>
          {textFormatter.formatCurrency(
            calculatePriceAfterDiscount(discount, getSearchData(price), false) * (quantity ?? 1),
            currency,
          )}
        </Text>
      </DataContainer>
    </CenterFlex>
  );
};
